// @flow

import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';

import {
  createResource,
  type FetchResponse,
  type Resource,
} from '@gandi/resource/redux';

import type {
  FullState,
  ThunkAction as BaseThunkAction,
  Dispatch,
} from '../../../types';
import type {
  CustomerDataForm,
  CustomerDetail,
  CustomersList,
  ListCustomer,
  CustomerListState,
  State as CustomersState,
} from '../types';

export type CreateCustomerAction<R> = (
  orgId: string,
  data: CustomerDataForm,
  options: {
    dryrun?: boolean,
  },
) => R;
export type EditCustomerAction<R> = (
  orgId: string,
  reselleeId: string,
  data: CustomerDataForm,
) => R;
export type FetchCustomerListAction<R> = (orgId: string, filter?: string) => R;
export type FetchOneCustomerAction<R> = (
  orgId: string,
  reselleeId: string,
) => R;

type ThunkAction = BaseThunkAction<Promise<any>>;

export const LIST_API_LIMIT = 50;
const EMPTY_ARRAY: CustomerListState = [];

// FIXME: Migrate to `@gandi/redux-organization` `customers` module.

// ********** RESOURCE **********

export const customersResource: Resource<> = createResource('customers');

// ********** ACTIONS **********

export const fetchCustomersList: FetchCustomerListAction<ThunkAction> =
  (orgId, filter = '') =>
  (dispatch: Dispatch) =>
    dispatch(
      customersResource.fetch(
        `/organization/organizations/${orgId}/resellees`,
        {
          label: orgId,
          params: {
            per_page: LIST_API_LIMIT,
            '~name': filter ? `*${filter}*` : undefined,
            with_product_count: 0,
          },
        },
      ),
    );

export const fetchOneCustomer: FetchOneCustomerAction<ThunkAction> =
  (orgId, reselleeId) => (dispatch: Dispatch) =>
    dispatch(
      customersResource.fetchOne(
        `/organization/organizations/${orgId}/resellees/${reselleeId}`,
        {
          label: reselleeId,
        },
      ),
    );

/**
 * Same has RFHandlers but writable and with FetchResponse
 */
type Handlers = {
  [string]: (response: FetchResponse<any, any>) => mixed,
};

export const createCustomer: CreateCustomerAction<ThunkAction> =
  (orgId, data, options = {}) =>
  (dispatch: Dispatch) => {
    const dryrun = Number(options.dryrun);
    const headers = { 'Dry-Run': dryrun };
    const failure = (response: FetchResponse<any, any>) =>
      Promise.reject(response);
    const handlers: Handlers = {};
    if (dryrun) {
      handlers.on200 = response => {
        // $FlowFixMe[sketchy-number-and]
        if (dryrun && Array.isArray(response.data.errors)) {
          return Promise.reject(response);
        }

        return Promise.resolve(response);
      };
      handlers.not200 = failure;
    } else {
      handlers.on201 = response => response;
      handlers.not201 = failure;
    }

    return dispatch(
      customersResource.createOne(
        `/organization/organizations/${orgId}/resellees`,
        data,
        // $FlowFixMe[cannot-spread-indexer] :shrug:
        {
          headers,
          ...handlers,
        },
      ),
    ).then(response => {
      if (!dryrun) {
        dispatch(customersResource.invalidate());
      }
      return response;
    });
  };

export const editCustomer: EditCustomerAction<ThunkAction> =
  (orgId: string, reselleeId: string, data: CustomerDataForm) =>
  (dispatch: Dispatch) =>
    dispatch(
      customersResource.patchOne(
        `/organization/organizations/${orgId}/resellees/${reselleeId}`,
        data,
        {
          // $FlowFixMe[incompatible-exact]
          on20X: (response: FetchResponse<>) => response,
          // $FlowFixMe[incompatible-exact]
          not20X: (response: FetchResponse<>) => Promise.reject(response),
        },
      ),
    ).then(() => dispatch(customersResource.invalidate()));

// ********** SELECTORS **********

const selectReselleeIdProp = (_: any, reselleeId: string): string => reselleeId;

export const selectCustomersList = (state: FullState): CustomerListState =>
  state.configure.customers.list;

export const selectOneListCustomer: (
  state: FullState,
  reselleeId: string,
  // $FlowFixMe[incompatible-call]
) => ListCustomer | void = createSelector(
  [selectCustomersList, selectReselleeIdProp],
  (list, reselleeId) => list.find(({ id }) => id === reselleeId),
);

export const selectOneCustomer = (
  state: FullState,
  reselleeId: string,
): CustomerDetail | ListCustomer | void =>
  state.configure.customers.detail[reselleeId] ||
  selectOneListCustomer(state, reselleeId);

export const selectCustomersCount = (state: FullState): number | void =>
  state.configure.customers.count;

export const selectIsFetchingCustomers = (
  state: FullState,
  orgId: string,
): boolean => customersResource.selectIsFetching(state, orgId);

export const selectIsLoadedCustomers = (
  state: FullState,
  orgId: string,
): boolean => customersResource.selectIsLoaded(state, orgId);

export const selectIsFailureCustomers = (
  state: FullState,
  orgId: string,
): boolean => customersResource.selectIsFailure(state, orgId);

export const selectIsCreatingCustomer: (
  state: FullState, // $FlowFixMe[incompatible-exact]
) => boolean = (state: FullState): boolean =>
  customersResource.selectIsCreatingOne(state, 'default');

export const selectIsEditingCustomer = (
  state: FullState,
  customerId: string,
): boolean => customersResource.selectIsPatchingOne(state, customerId);

export const selectIsToRefetchCustomers = (
  state: FullState,
  orgId: string,
): boolean => customersResource.selectIsToRefetch(state, orgId);

// ********** HANDLERS **********

const fetchOneCustomerHandler = (
  state: CustomersState,
  {
    response,
    label,
  }: {
    response: FetchResponse<200, CustomersList>,
    label: string,
  },
): {
  count: number | void,
  detail: { [string]: CustomerDetail, ... },
  list: CustomerListState,
  ...
} => ({
  ...state,
  detail: {
    ...state.detail,
    // $FlowFixMe[incompatible-type] TODO: Fix type
    [label]: response.data,
  },
});

const fetchCustomersListHandler = (
  state: CustomersState,
  {
    response,
  }: {
    response: FetchResponse<200, CustomersList>,
  },
): {
  count: number,
  detail: { [reselleeId: string]: CustomerDetail, ... },
  list: Array<{
    city: string,
    country: string,
    email: string,
    fax?: string,
    id: string,
    key: string,
    name: string,
    phone: string,
    product_count?: number,
    state?: string,
    status: string,
    streetaddr: string,
    streetaddr2?: string,
    type: string,
    zip: string,
  }>,
  ...
} => {
  const customersList = response.data.map(customer => ({
    key: customer.id,
    ...customer,
  }));

  return {
    ...state,
    list: customersList,
    // $FlowFixMe[incompatible-call]
    count: Number.parseInt(response.headers.get('X-Total-Count'), 10),
  };
};

// ********** REDUCER **********

const initialState: CustomersState = {
  detail: {},
  list: EMPTY_ARRAY,
  count: undefined,
};

const reducer: {
  (): (state: CustomersState, payload?: mixed, meta?: mixed) => CustomersState,
  assignAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  batch: ({ ... } | Array<mixed>) => mixed,
  bindAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  disbatch: (mixed, void | Array<mixed>) => mixed,
  has: (actionCreator: mixed) => boolean,
  // $FlowFixMe[cannot-resolve-name]
  off: Reducer$Off,
  // $FlowFixMe[cannot-resolve-name]
  on: Reducer$On<CustomersState>,
  options: () => mixed,
  ...
} = createReducer(
  {
    [customersResource.fetch.success.getType()]: fetchCustomersListHandler,
    // $FlowFixMe[incompatible-type] TODO: Fix type
    // $FlowFixMe[prop-missing] TODO: Fix type
    [customersResource.fetchOne.success.getType()]: fetchOneCustomerHandler,
  },
  initialState,
);

export default reducer;
